import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import { badgeClass, formatMoney, formatNumber, inputClass } from '../../components/ui/FormControls';
import { usePermissions } from '../../hooks/usePermissions';
import { cn } from '../../lib/utils';
import AttendancePanel from './AttendancePanel';
import Employees from './Employees';
import HrSetup from './HrSetup';
import PayrollPanel from './PayrollPanel';
import { day, lastMonth, monthName, tdClass, thClass } from './hrShared';

type Tab = 'employees' | 'attendance' | 'payroll' | 'contract' | 'setup';

const ContractLabour: React.FC = () => {
  const [month, setMonth] = useState(lastMonth());
  const { data, isLoading } = useQuery<{ contractors: any[] }>({ queryKey: ['hr', 'contract-labour', month], queryFn: async () => (await api.get('/hr/contract-labour', { params: { month } })).data });
  return (
    <div className="space-y-4">
      <div className="flex items-end gap-3">
        <label className="text-sm text-gray-700">Month<input type="month" value={month} onChange={(event) => setMonth(event.target.value)} aria-label="Contract labour month" className={cn(inputClass, 'mt-1 w-44')} /></label>
        <p className="text-sm text-gray-500">Man-days and overtime of each contractor&apos;s workers, priced at the rates agreed with the contractor.</p>
      </div>
      {isLoading || !data ? <p className="p-8 text-center text-sm text-gray-500">Loading…</p> : data.contractors.length === 0 ? <p className="rounded-lg bg-white p-8 text-center text-sm text-gray-500 ring-1 ring-gray-200">No contract workers in {monthName(month)}.</p> : data.contractors.map((contractor) => (
        <div key={contractor.id} className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200" data-testid="contractor-statement">
          <div className="flex flex-wrap items-baseline justify-between gap-3 border-b border-gray-200 px-4 py-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">{contractor.name}</h3>
              <p className="text-xs text-gray-500">{contractor.licenceNumber ? `Licence ${contractor.licenceNumber}${contractor.licenceValidUntil ? ` until ${day(contractor.licenceValidUntil)}` : ''}` : 'No licence entered'}{contractor.licenceExpired && <span className={cn(badgeClass, 'ml-2 bg-red-50 text-red-700')}>Licence expired</span>}</p>
            </div>
            <p className="text-sm text-gray-700">{formatNumber(contractor.manDays)} man-days · {contractor.overtimeHours} OT h · <span className="font-semibold">{contractor.amount === null ? 'no rate agreed' : formatMoney(contractor.amount)}</span></p>
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50"><tr><th className={thClass}>Worker</th><th className={cn(thClass, 'text-right')}>Man-days</th><th className={cn(thClass, 'text-right')}>Overtime h</th></tr></thead>
            <tbody className="divide-y divide-gray-100">
              {contractor.workers.map((worker: any) => (
                <tr key={worker.employeeId}><td className={tdClass}><span className="font-mono text-xs text-gray-500">{worker.code}</span> {worker.name}</td><td className={cn(tdClass, 'text-right tabular-nums')}>{worker.manDays}</td><td className={cn(tdClass, 'text-right tabular-nums')}>{worker.overtimeHours}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
};

const Hr: React.FC = () => {
  const { can } = usePermissions();
  const [tab, setTab] = useState<Tab>('employees');
  const { data: summary } = useQuery<{ permanent: number; contract: number; presentToday: number; notMarkedToday: number }>({ queryKey: ['hr', 'summary'], queryFn: async () => (await api.get('/hr/summary')).data });
  const tabs: { key: Tab; label: string }[] = [
    { key: 'employees', label: 'Employees' },
    { key: 'attendance', label: 'Attendance' },
    { key: 'payroll', label: 'Payroll' },
    { key: 'contract', label: 'Contract Labour' },
    { key: 'setup', label: 'Setup' },
  ];
  const stats = [
    { label: 'On the rolls', value: summary?.permanent ?? 0 },
    { label: 'Contract workers', value: summary?.contract ?? 0 },
    { label: 'Present today', value: summary?.presentToday ?? 0 },
    { label: 'Not marked today', value: summary?.notMarkedToday ?? 0, alert: (summary?.notMarkedToday ?? 0) > 0 },
  ];
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">HR &amp; Payroll</h1>
        <p className="mt-1 text-sm text-gray-500">Employees and contract workers, shifts and attendance, and the monthly payroll worked out with your own pay components.</p>
      </div>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.label} className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{stat.label}</p>
            <p className={cn('mt-1 text-2xl font-semibold', stat.alert ? 'text-amber-600' : 'text-gray-900')}>{formatNumber(stat.value)}</p>
          </div>
        ))}
      </div>
      <div className="flex gap-1 overflow-x-auto border-b border-gray-200" role="tablist">
        {tabs.map((item) => (
          <button key={item.key} type="button" role="tab" aria-selected={tab === item.key} onClick={() => setTab(item.key)} className={cn('-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium', tab === item.key ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700')}>{item.label}</button>
        ))}
      </div>
      {tab === 'employees' && <Employees canEdit={can('hr.update') && can('hr.create')} />}
      {tab === 'attendance' && <AttendancePanel canEdit={can('hr.update')} />}
      {tab === 'payroll' && <PayrollPanel canEdit={can('hr.update') && can('hr.create')} canFinalise={can('hr.approve')} canExport={can('hr.export')} />}
      {tab === 'contract' && <ContractLabour />}
      {tab === 'setup' && <HrSetup canEdit={can('hr.update') && can('hr.create')} />}
    </div>
  );
};

export default Hr;
